import { memo, useEffect, useMemo } from 'react';
import { useUpdateNodeInternals } from '@xyflow/react';
import { HANDLES_TYPES, NODES_TYPES } from '../../types';
import { buildHandleId } from '../../lib/utils';
import CustomNode from './CustomNode';
import HandlesWrapper from '../HandlesWrapper';
import styles from '../../timeline-node-manager.module.css';
import {
  ASSET_CATEGORY_TYPES,
  ASSET_PREVIEW_TYPES,
} from '../../../../../types/assetTypes';

interface Asset {
  id: string;
  name: string;
  url: string;
  type?: string;
  previewImage?: string;
  ref: {
    current: {
      uuid: string;
    };
  };
}

interface SceneAssetProps {
  position?: any;
  scale?: any;
  rotation?: any;
  instanceId: string;
  asset: Asset;
}

interface ReactElement {
  props: SceneAssetProps;
  type: any;
  key: string;
}

interface NodeData {
  objectId?: string;
  previewImage?: string;
  type?: string;
}

interface TargetNodeProps {
  id: string;
  data: NodeData;
  type: NODES_TYPES;
  selected: boolean;
  selectableAssets: ReactElement[];
}

const TargetNode = ({
  id,
  data,
  type,
  selected,
  selectableAssets = [],
}: TargetNodeProps) => {
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    updateNodeInternals(id);
  }, [data]);

  const previewImage = useMemo(() => {
    if (!data.objectId || !selectableAssets?.length) {
      return null;
    }

    const selectedAsset = selectableAssets.find((asset) => {
      const assetId = asset.props.asset?.id;
      const assetUuid = asset.props.asset?.ref?.current?.uuid;
      const instanceId = asset.props.instanceId;
      return (
        assetId === data.objectId ||
        assetUuid === data.objectId ||
        instanceId === data.objectId
      );
    });

    if (!selectedAsset?.props?.asset) {
      return data.previewImage || null;
    }

    const assetData = selectedAsset.props.asset;
    if (
      (assetData.type === ASSET_CATEGORY_TYPES.MODELS ||
        assetData.type === 'OTHER') &&
      assetData.url &&
      assetData.url.includes('/assets/')
    ) {
      return ASSET_PREVIEW_TYPES.MODEL_PREVIEW;
    }

    return data.previewImage || assetData.previewImage || assetData.url;
  }, [data.objectId, selectableAssets]);

  return (
    <CustomNode
      id={id}
      data={data}
      label='Target'
      type={type}
      containerClassname={styles.targetNode}
      selected={selected}
    >
      <HandlesWrapper
        sources={[
          {
            label: 'Action',
            id: buildHandleId({
              type: 'input',
              handleType: HANDLES_TYPES.ACTION,
              nodeId: id,
            }),
          },
        ]}
      />
      {data.objectId && (
        <>
          {previewImage === ASSET_PREVIEW_TYPES.MODEL_PREVIEW ? (
            <div className={styles.targetPreviewContainer}>
              <div className={styles.modelPreview}>
                <div className={styles.modelIcon}>📦</div>
              </div>
            </div>
          ) : previewImage ? (
            <div className={styles.targetPreviewContainer}>
              <img
                src={previewImage}
                alt='Target preview'
                className={styles.targetPreviewImage}
                onError={(e) => {
                  console.error('Error loading preview image:', e);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <span className={styles.targetId}>{data.objectId}</span>
          )}
        </>
      )}
    </CustomNode>
  );
};

export default memo(TargetNode);
